// ============================================================
// COLLECTING FROM THE LOGS - every line newer than the marks, read once
// (v8.326.0 as the impacts' own; generalised for the walks, plan design-gaps M1).
//
// Two sources: each tab's own log (my own lines, and everything in solo) and
// the shared log (the table's lines, stamped again by the server). Both are
// watched by a high-water mark, so history never replays: a tab seen for the
// first time starts at `now`. The shared log is not delivered in stamp order,
// and a line can beat its combatant to this screen, so the lines of the last
// LATE_MS are read again and the ids read within that window remembered -
// each kept once. What a line READS AS is the caller's: an impact
// (engine/impacts), a move (engine/moveBeat). The one convention shared is
// the id, `${who}:${at}` with the line's own time last: the window is kept
// by it.
//
// Pure. Zero React dependency.
// ============================================================
import type { Tab } from '../../types';

/** Per-tab high-water marks for the local logs, one for the shared log, and the shared window's ids. */
export interface LogMarks {
  local: Record<string, number>;
  shared: number;
  /**
   * The shared lines already read that are stamped within LATE_MS of the mark. Lines from several writers are
   * not delivered in stamp order, so a line a little older than the newest one read is still read once - not
   * skipped for ever (the fuzzer's beat oracle found it, plan map-display D).
   */
  sharedSeen?: readonly string[];
}

/** A line as the collector needs it: when it was stamped; the rest is the reader's. */
export interface LogLine {
  timestamp?: number;
  [key: string]: unknown;
}

export interface LogReaders<T> {
  /** A tab's own line, the tab's id given (a local log's lines are about its target). Null: not one of ours. */
  local: (entry: LogLine, tabId: string) => T | null;
  /** A shared line. Null: not one of ours, or about nobody here. */
  shared: (entry: LogLine) => T | null;
}

/** How much older than the newest shared line read a newly delivered one may be and still count, ms. */
const LATE_MS = 5000;

/** The line's own time, off the id's tail. */
const atOf = (id: string) => Number(id.split(':').at(-1));

/**
 * Everything newer than the marks, each once by id, and the marks moved up.
 */
export function collectFromLogs<T extends { id: string }>(
  tabs: readonly Tab[],
  shared: readonly LogLine[],
  marks: LogMarks,
  now: number,
  read: LogReaders<T>,
): { found: T[]; marks: LogMarks } {
  const found: T[] = [];
  const seen = new Set<string>();
  // The same line seen through two sources (or twice) is one.
  const keep = (item: T | null) => {
    if (!item || seen.has(item.id)) return;
    seen.add(item.id);
    found.push(item);
  };
  const local: Record<string, number> = { ...marks.local };
  for (const tab of tabs) local[tab.id] = scanTab(tab, local[tab.id] ?? now, read.local, keep);
  const { mark, sharedSeen } = scanShared(shared, marks, read.shared, keep);
  return { found, marks: { local, shared: mark, sharedSeen } };
}

/** A tab's own log past its mark; returns the new mark. */
function scanTab<T>(tab: Tab, mark: number, read: LogReaders<T>['local'], keep: (item: T | null) => void): number {
  let latest = mark;
  for (const entry of (tab.narrativeLog ?? []) as LogLine[]) {
    if ((entry.timestamp ?? 0) <= mark) continue;
    latest = Math.max(latest, entry.timestamp as number);
    keep(read(entry, tab.id));
  }
  return latest;
}

/** The shared log: every line past the mark and the window's again, each kept once; the new mark and window. */
function scanShared<T extends { id: string }>(
  shared: readonly LogLine[],
  marks: LogMarks,
  read: LogReaders<T>['shared'],
  keep: (item: T | null) => void,
): { mark: number; sharedSeen: string[] } {
  const was = new Set(marks.sharedSeen ?? []);
  const fresh = shared
    .filter((entry) => (entry.timestamp ?? 0) > marks.shared - LATE_MS)
    .map(read)
    .filter((item): item is T => !!item && !was.has(item.id));
  fresh.forEach(keep);
  let mark = marks.shared;
  for (const e of shared) mark = Math.max(mark, e.timestamp ?? 0);
  const sharedSeen = [...was, ...fresh.map((i) => i.id)].filter((id) => atOf(id) > mark - LATE_MS);
  return { mark, sharedSeen };
}
